import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import { ArrowLeft, Edit, Download, Building2, Mail, Phone, MapPin, Calendar, Users, FileText, Search, Video, Eye } from 'lucide-react';
import Card from '../../components/Common/Card';
import Button from '../../components/Common/Button';
import Badge from '../../components/Common/Badge';
import Modal from '../../components/Common/Modal';
import api, { getFileUrl } from '../../utils/api';
import toast from 'react-hot-toast';
import { format } from 'date-fns';
import jsPDF from 'jspdf';
import 'jspdf-autotable';
import * as XLSX from 'xlsx';

const CollegeProfile = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [college, setCollege] = useState(null);
  const [users, setUsers] = useState([]);
  const [courses, setCourses] = useState([]);
  const [loading, setLoading] = useState(true);
  const [activeTab, setActiveTab] = useState('overview');
  const [studentSearch, setStudentSearch] = useState('');
  const [previewDoc, setPreviewDoc] = useState(null);

  const onboardingDateVal = college ? (college.onboardingDate || college.onboardDate || college.createdAt) : null;

  useEffect(() => {
    fetchCollegeData();
  }, [id]);

  const fetchCollegeData = async () => {
    if (!id || id === 'null' || id === 'undefined') {
      setLoading(false);
      return;
    }
    try {
      // Fetch college details
      const collegeRes = await api.get(`/colleges/${id}`);
      if (collegeRes.data.success) {
        setCollege(collegeRes.data.data);
      }

      // Fetch users for this college
      const usersRes = await api.get(`/users?college=${id}&role=admin,college_admin,student&limit=1000`);
      if (usersRes.data.success) {
        setUsers(usersRes.data.data);
      }

      // Fetch courses for this college
      const coursesRes = await api.get(`/courses?college=${id}`);
      if (coursesRes.data.success) {
        setCourses(coursesRes.data.data);
      }
    } catch (error) {
      toast.error('Failed to fetch college data');
    } finally {
      setLoading(false);
    }
  };

  const exportCollegeSummaryPDF = () => {
   try {
    const doc = new jsPDF();

    // Header
    doc.setFontSize(20);
    doc.text('College Profile Summary', 14, 22);
    doc.setFontSize(11);
    doc.text(`Generated on: ${format(new Date(), 'dd/MM/yyyy HH:mm')}`, 14, 30);

    // College Info
    doc.setFontSize(14);
    doc.text('College Information', 14, 45);
    doc.setFontSize(10);

    const collegeInfo = [
      ['College Name', college.collegeName],
      ['College Code', college.collegeCode || 'N/A'],
      ['Email', college.email],
      ['Contact', college.contactNumber],
      ['Institution Type', college.institutionType],
      ['Onboarding Date', onboardingDateVal ? format(new Date(onboardingDateVal), 'dd/MM/yyyy') : 'N/A'],
      ['Status', college.status],
    ];

    doc.autoTable({
      body: collegeInfo,
      startY: 50,
      theme: 'plain',
      styles: { fontSize: 10 },
    });

    // Users Summary
    const yPos = doc.lastAutoTable.finalY + 15;
    doc.setFontSize(14);
    doc.text('Users Summary', 14, yPos);

    const userStats = [
      ['Total Users', users.length.toString()],
      ['College Admins', users.filter(u => ['admin', 'college_admin'].includes(u.role)).length.toString()],
      ['Students', users.filter(u => u.role === 'student').length.toString()],
      ['Active Users', users.filter(u => u.status?.toLowerCase() === 'active').length.toString()],
    ];

    doc.autoTable({
      body: userStats,
      startY: yPos + 5,
      theme: 'plain',
      styles: { fontSize: 10 },
    });

    doc.save(`${college.collegeCode || 'institute'}-profile-${format(new Date(), 'yyyy-MM-dd')}.pdf`);
    toast.success('PDF exported successfully');
   } catch (err) {
    toast.error('Failed to export PDF');
   }
  };

  const exportCollegeSummaryExcel = () => {
   try {
    const wb = XLSX.utils.book_new();

    // College Info Sheet
    const collegeData = [
      ['College Information'],
      ['College Name', college.collegeName],
      ['College Code', college.collegeCode || 'N/A'],
      ['Email', college.email],
      ['Contact', college.contactNumber],
      ['Institution Type', college.institutionType],
      ['Onboarding Date', onboardingDateVal ? format(new Date(onboardingDateVal), 'dd/MM/yyyy') : 'N/A'],
      ['Status', college.status],
      [],
      ['Address'],
      ['Street', college.address?.street || 'N/A'],
      ['City', college.address?.city || 'N/A'],
      ['State', college.address?.state || 'N/A'],
      ['Pincode', college.address?.pincode || 'N/A'],
    ];
    const ws1 = XLSX.utils.aoa_to_sheet(collegeData);
    XLSX.utils.book_append_sheet(wb, ws1, 'College Info');

    // Users Sheet
    const usersData = users.map(user => ({
      'User ID': user.userId || 'N/A',
      'Name': user.fullName,
      'Email': user.email,
      'Role': user.role,
      'Status': user.status,
    }));
    const ws2 = XLSX.utils.json_to_sheet(usersData);
    XLSX.utils.book_append_sheet(wb, ws2, 'Users');

    XLSX.writeFile(wb, `${college.collegeCode || 'institute'}-profile-${format(new Date(), 'yyyy-MM-dd')}.xlsx`);
    toast.success('Excel exported successfully');
   } catch (err) {
    toast.error('Failed to export Excel');
   }
  };

  if (loading) {
    return (
      <div className="space-y-6 animate-pulse">
        <div className="h-16 bg-gray-200 dark:bg-[#002147] rounded-xl w-full border border-gray-100 dark:border-[#1a3884]/30"></div>
        <div className="flex space-x-4">
          <div className="h-10 w-24 bg-gray-200 dark:bg-[#002147] rounded-full border border-gray-100 dark:border-[#1a3884]/30"></div>
          <div className="h-10 w-32 bg-gray-200 dark:bg-[#002147] rounded-full border border-gray-100 dark:border-[#1a3884]/30"></div>
          <div className="h-10 w-24 bg-gray-200 dark:bg-[#002147] rounded-full border border-gray-100 dark:border-[#1a3884]/30"></div>
        </div>
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
          <div className="h-64 bg-gray-200 dark:bg-[#002147] rounded-xl border border-gray-100 dark:border-[#1a3884]/30"></div>
          <div className="h-64 bg-gray-200 dark:bg-[#002147] rounded-xl border border-gray-100 dark:border-[#1a3884]/30"></div>
        </div>
      </div>
    );
  }

  if (!college) {
    return (
      <div className="flex flex-col items-center justify-center h-64 text-center">
        <Building2 className="w-16 h-16 text-gray-300 dark:text-[#1a3884]/40 mb-4" />
        <p className="text-xl font-semibold text-gray-700 dark:text-gray-300">Institute not found</p>
        <p className="text-sm text-gray-500 dark:text-gray-400 mt-2 mb-6">The institute you are looking for does not exist or has been removed.</p>
        <Button onClick={() => navigate('/colleges')} icon={ArrowLeft}>
          Back to Directory
        </Button>
      </div>
    );
  }

  const tabs = [
    { id: 'overview', label: 'Overview', icon: Building2 },
    { id: 'users', label: 'Users Summary', icon: Users },
    { id: 'reports', label: 'Reports', icon: FileText },
  ];

  const filteredStudents = users.filter(u => 
    u.role === 'student' && 
    (u.fullName.toLowerCase().includes(studentSearch.toLowerCase()) || 
     u.email.toLowerCase().includes(studentSearch.toLowerCase()))
  );

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 bg-white dark:bg-[#002147] px-5 py-4 rounded-2xl shadow-sm border border-gray-100 dark:border-[#1a3884]/35 transition-colors">
        <div className="flex items-center gap-3 min-w-0">
          <button
            onClick={() => navigate('/colleges')}
            className="p-2 bg-gray-50 dark:bg-[#00152E]/50 text-gray-500 dark:text-gray-300 hover:text-[#1a3884] dark:hover:text-blue-400 hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/20 rounded-lg transition-all border border-gray-200 dark:border-[#1a3884]/45 group shrink-0"
            title="Back to Directory"
          >
            <ArrowLeft className="w-4 h-4 group-hover:-translate-x-0.5 transition-transform" />
          </button>
          <div className="w-11 h-11 rounded-xl bg-white border border-gray-200 dark:border-[#1a3884]/40 flex items-center justify-center shadow-sm shrink-0 overflow-hidden p-1">
            {college.logo ? (
              <img src={getFileUrl(college.logo)} alt={college.collegeName} className="w-full h-full object-contain" />
            ) : (
              <Building2 className="w-5 h-5 text-[#1a3884]" />
            )}
          </div>
          <div className="min-w-0">
            <h1 className="text-lg sm:text-xl font-bold text-gray-900 dark:text-white leading-tight truncate">
              {college.collegeName}
            </h1>
            <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 mt-0.5 font-medium truncate">
              {college.collegeCode || 'Pending Code'} &bull; {college.institutionType}
            </p>
          </div>
        </div>
        <button
          onClick={() => navigate(`/colleges/edit/${id}`)}
          className="inline-flex items-center gap-1.5 self-end sm:self-auto shrink-0 px-3.5 py-2 rounded-lg text-sm font-semibold text-[#1a3884] dark:text-blue-300 bg-white dark:bg-[#00152e] border border-gray-200 dark:border-[#1a3884]/45 hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/20 transition-colors"
        >
          <Edit className="w-3.5 h-3.5" />
          Edit Details
        </button>
      </div>

      {/* Tabs */}
      <div className="overflow-x-auto scrollbar-none pb-2">
        <nav className="flex space-x-2 min-w-max">
          {tabs.map((tab) => {
            const Icon = tab.icon;
            return (
              <button
                key={tab.id}
                onClick={() => setActiveTab(tab.id)}
                className={`flex items-center gap-1.5 py-1.5 px-3.5 rounded-lg font-semibold text-[13px] transition-all ${
                  activeTab === tab.id
                    ? 'bg-[#1a3884] text-white shadow-sm'
                    : 'bg-white dark:bg-[#002147] text-gray-600 dark:text-gray-300 hover:bg-gray-50 dark:hover:bg-[#001f40] border border-gray-200 dark:border-[#1a3884]/35'
                }`}
              >
                <Icon className={`w-3.5 h-3.5 ${activeTab === tab.id ? 'text-blue-200' : 'text-gray-400 dark:text-gray-500'}`} />
                <span>{tab.label}</span>
              </button>
            );
          })}
        </nav>
      </div>

      {/* Tab Content */}
      {activeTab === 'overview' && (
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-6 animate-fade-in">
          {/* Basic Information */}
          <Card title="Basic Information">
            <div className="space-y-5">
              <div className="flex items-start">
                <Building2 className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 mr-3" />
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">College Name</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{college.collegeName}</p>
                </div>
              </div>

              <div className="flex items-start">
                <Mail className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 mr-3" />
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Email</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{college.email}</p>
                </div>
              </div>

              <div className="flex items-start">
                <Phone className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 mr-3" />
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Contact Number</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{college.contactNumber}</p>
                </div>
              </div>

              <div className="flex items-start">
                <MapPin className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 mr-3" />
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Address</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white leading-relaxed">
                    {college.address?.street && `${college.address.street}, `}
                    {college.address?.city && `${college.address.city}, `}
                    {college.address?.state && `${college.address.state} - `}
                    {college.address?.pincode}
                  </p>
                </div>
              </div>

              <div className="flex items-start">
                <Calendar className="w-5 h-5 text-gray-400 dark:text-gray-500 mt-0.5 mr-3" />
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Date of Onboarding</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">
                    {onboardingDateVal ? format(new Date(onboardingDateVal), 'dd MMMM yyyy') : 'N/A'}
                  </p>
                </div>
              </div>
            </div>
          </Card>

          {/* Institution Details */}
          <div className="space-y-6">
            <Card title="Institution Details">
              <div className="grid grid-cols-2 gap-6">
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">College Code</p>
                  <p className="text-sm font-mono font-bold text-gray-900 dark:text-white bg-gray-50 dark:bg-[#00152E]/50 px-3 py-1.5 rounded-lg inline-block border border-gray-100 dark:border-[#1a3884]/20">{college.collegeCode || 'Pending'}</p>
                </div>
                
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Status</p>
                  <Badge variant={college.status?.toLowerCase() === 'active' ? 'success' : college.status?.toLowerCase() === 'inactive' ? 'danger' : 'warning'}>
                    {college.status}
                  </Badge>
                </div>

                <div className="col-span-2">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-2">Institution Type</p>
                  <Badge variant={
                    college.institutionType?.includes('University') ? 'primary' :
                      college.institutionType?.includes('Autonomous') ? 'success' : 'secondary'
                  }>
                    {college.institutionType}
                  </Badge>
                </div>
              </div>
            </Card>

            {/* Coordinator Information */}
            <Card title="Coordinator Information">
              <div className="grid grid-cols-2 gap-y-5 gap-x-4">
                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Name</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{college.coordinatorInfo?.name || 'N/A'}</p>
                </div>

                <div>
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Designation</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{college.coordinatorInfo?.designation || 'N/A'}</p>
                </div>

                <div className="col-span-2">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Email</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{college.coordinatorInfo?.email || 'N/A'}</p>
                </div>

                <div className="col-span-2">
                  <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Mobile</p>
                  <p className="text-sm font-medium text-gray-900 dark:text-white">{college.coordinatorInfo?.mobile || 'N/A'}</p>
                </div>
              </div>
            </Card>
          </div>

          {/* Admin Details */}
          <Card title="Admin Contact Information">
            <div className="space-y-5">
              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Admin Name</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{college.collegeAdmin?.fullName || college.collegeAdmin?.name || 'N/A'}</p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Admin Email</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{college.collegeAdmin?.email || 'N/A'}</p>
              </div>

              <div>
                <p className="text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase tracking-wider mb-1">Admin Mobile</p>
                <p className="text-sm font-medium text-gray-900 dark:text-white">{college.collegeAdmin?.mobile || 'N/A'}</p>
              </div>
            </div>
          </Card>

          {/* Documents */}
          <Card title="Uploaded Documents & Media">
            <div className="space-y-3">
              {(() => {
                const docItems = [
                  { key: 'mouDocument', title: 'MoU / Authorization Letter', subtitle: 'Verification Certificate', icon: FileText, type: 'image', iconClass: 'bg-blue-100 dark:bg-blue-900/30 text-blue-600 dark:text-blue-400' },
                  { key: 'universityActProof', title: 'Registration Proof', subtitle: 'University Act Proof', icon: FileText, type: 'image', iconClass: 'bg-green-100 dark:bg-green-900/30 text-green-600 dark:text-green-400' },
                  { key: 'ndaDocument', title: 'NDA / Service Agreement', subtitle: 'Service Contract', icon: FileText, type: 'image', iconClass: 'bg-purple-100 dark:bg-purple-900/30 text-purple-600 dark:text-purple-400' },
                  { key: 'chairmanVideo', title: 'Principal / Chairman Video', subtitle: 'Introduction Video', icon: Video, type: 'video', iconClass: 'bg-rose-100 dark:bg-rose-900/30 text-rose-600 dark:text-rose-400' },
                ];
                const available = docItems.filter(d => college[d.key]);

                if (available.length === 0) {
                  return (
                    <div className="text-center py-6 border-2 border-dashed border-gray-200 dark:border-[#1a3884]/30 rounded-xl">
                      <FileText className="w-8 h-8 mx-auto text-gray-300 dark:text-[#1a3884]/50 mb-2" />
                      <p className="text-sm text-gray-500 dark:text-gray-400">No documents uploaded</p>
                    </div>
                  );
                }

                return available.map((d) => {
                  const Icon = d.icon;
                  const url = getFileUrl(college[d.key]);
                  return (
                    <div key={d.key} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 bg-gray-50 dark:bg-[#00152E]/30 rounded-xl hover:bg-gray-100 dark:hover:bg-[#1a3884]/20 border border-transparent dark:border-[#1a3884]/20 transition-all">
                      <div className="flex items-start space-x-3 min-w-0">
                        <div className={`p-1.5 rounded-lg shrink-0 ${d.iconClass}`}>
                          <Icon className="w-4 h-4" />
                        </div>
                        <div className="min-w-0">
                          <p className="text-sm font-semibold text-gray-900 dark:text-white truncate">{d.title}</p>
                          <p className="text-xs text-gray-500 dark:text-gray-400 mt-0.5">{d.subtitle}</p>
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 w-full sm:w-auto shrink-0">
                        <button
                          type="button"
                          onClick={() => setPreviewDoc({ title: d.title, url, type: d.type })}
                          className="inline-flex items-center justify-center gap-1 flex-1 sm:flex-none px-2.5 py-1.5 rounded-lg text-xs font-semibold bg-[#1a3884] text-white hover:bg-[#0f225c] transition-colors"
                        >
                          <Eye className="w-3.5 h-3.5" /> View
                        </button>
                        <button
                          type="button"
                          onClick={() => window.open(url, '_blank')}
                          className="inline-flex items-center justify-center gap-1 flex-1 sm:flex-none px-2.5 py-1.5 rounded-lg text-xs font-semibold text-[#1a3884] dark:text-blue-300 bg-white dark:bg-[#00152e] border border-gray-200 dark:border-[#1a3884]/45 hover:bg-[#1a3884]/5 dark:hover:bg-[#1a3884]/25 transition-colors"
                        >
                          <Download className="w-3.5 h-3.5" /> Download
                        </button>
                      </div>
                    </div>
                  );
                });
              })()}
            </div>
          </Card>
        </div>
      )}

      {activeTab === 'users' && (
        <div className="grid grid-cols-1 lg:grid-cols-3 gap-6 animate-fade-in">
          {/* Statistics */}
          <Card title="User Statistics">
            {/* One deep-navy family instead of the old blue/green/purple/amber
                rainbow — total leads as a solid navy tile, the breakdown rows
                sit quietly beneath it. */}
            <div className="space-y-3">
              <div className="flex items-center justify-between px-4 py-4 bg-gradient-to-br from-[#00152E] to-[#1a3884] rounded-xl shadow-sm">
                <span className="text-sm font-semibold text-blue-100">Total Users</span>
                <span className="text-2xl font-bold text-white">{users.length}</span>
              </div>

              {[
                { label: 'College Admins', value: users.filter(u => ['admin', 'college_admin'].includes(u.role)).length },
                { label: 'Students', value: users.filter(u => u.role === 'student').length },
                { label: 'Active Users', value: users.filter(u => u.status?.toLowerCase() === 'active').length }
              ].map(({ label, value }) => (
                <div key={label} className="flex items-center justify-between px-4 py-3 bg-[#1a3884]/5 dark:bg-[#1a3884]/15 border border-[#1a3884]/10 dark:border-[#1a3884]/40 rounded-xl transition-colors">
                  <span className="text-sm font-medium text-gray-600 dark:text-gray-300">{label}</span>
                  <span className="text-lg font-bold text-[#1a3884] dark:text-[#4f7dff]">{value}</span>
                </div>
              ))}
            </div>
          </Card>

          {/* Users List */}
          <div className="lg:col-span-2 space-y-6">
            <Card title="Admins & Staff">
              <div className="space-y-3 max-h-80 overflow-y-auto custom-scrollbar pr-2">
                {users.filter(u => u.role !== 'student').length > 0 ? (
                  users.filter(u => u.role !== 'student').map((user) => (
                    <div key={user._id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-gray-100 dark:border-[#1a3884]/30 rounded-xl hover:bg-gray-50 dark:hover:bg-[#1a3884]/10 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 bg-[#1a3884]/10 dark:bg-[#1a3884]/30 rounded-lg flex items-center justify-center shrink-0">
                          <span className="text-[#1a3884] dark:text-[#4f7dff] font-semibold text-sm">
                            {user.fullName?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">{user.fullName}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{user.email}</p>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 self-start sm:self-auto">
                        <Badge variant={
                          user.role === 'college_admin' ? 'primary' : 'secondary'
                        }>
                          {user.role}
                        </Badge>
                        <Badge variant={user.status?.toLowerCase() === 'active' ? 'success' : 'warning'}>
                          {user.status}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-8">
                     <p className="text-gray-500 dark:text-gray-400">No non-student users found for this college</p>
                  </div>
                )}
              </div>
            </Card>

            <Card title={
              <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3">
                <span>Students List</span>
                <div className="relative">
                   <Search className="w-4 h-4 text-gray-400 absolute left-3 top-1/2 transform -translate-y-1/2" />
                   <input
                     type="text"
                     placeholder="Search students..."
                     value={studentSearch}
                     onChange={(e) => setStudentSearch(e.target.value)}
                     className="pl-9 pr-4 py-1.5 text-sm border border-gray-200 dark:border-[#1a3884]/40 bg-gray-50 dark:bg-[#00152E]/50 text-gray-900 dark:text-white rounded-lg focus:outline-none focus:ring-2 focus:ring-[#1a3884] font-normal w-full sm:w-auto"
                   />
                </div>
              </div>
            }>
              <div className="space-y-3 max-h-[400px] overflow-y-auto custom-scrollbar pr-2">
                {filteredStudents.length > 0 ? (
                  filteredStudents.map((student) => (
                    <div key={student._id} className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-3 border border-gray-100 dark:border-[#1a3884]/30 rounded-xl hover:bg-gray-50 dark:hover:bg-[#1a3884]/10 transition-colors">
                      <div className="flex items-center space-x-3">
                        <div className="w-9 h-9 bg-[#1a3884]/10 dark:bg-[#1a3884]/30 rounded-lg flex items-center justify-center shrink-0">
                          <span className="text-[#1a3884] dark:text-[#4f7dff] font-semibold text-sm">
                            {student.fullName?.charAt(0).toUpperCase()}
                          </span>
                        </div>
                        <div className="min-w-0">
                          <p className="font-medium text-gray-900 dark:text-white truncate">{student.fullName}</p>
                          <p className="text-sm text-gray-500 dark:text-gray-400 truncate">{student.email}</p>
                          <div className="flex flex-wrap items-center gap-x-2 gap-y-0.5 mt-1 text-xs text-gray-500 dark:text-gray-400">
                            {student.rollNumber && <span>Roll No: {student.rollNumber}</span>}
                            {student.department && <span>Dept: {student.department?.fullName || student.department?.abbreviation || student.department}</span>}
                            {(student.rollNumber || student.department) && student.batch && <span>•</span>}
                            {student.batch && <span>Batch: {student.batch}</span>}
                          </div>
                        </div>
                      </div>
                      <div className="flex items-center space-x-2 self-start sm:self-auto shrink-0">
                        <Badge variant="success">
                          Student
                        </Badge>
                        <Badge variant={student.status?.toLowerCase() === 'active' ? 'success' : 'warning'}>
                          {student.status}
                        </Badge>
                      </div>
                    </div>
                  ))
                ) : (
                  <div className="text-center py-12 border border-dashed border-gray-200 dark:border-[#1a3884]/30 rounded-xl bg-gray-50/50 dark:bg-[#00152E]/30">
                     <Users className="w-10 h-10 mx-auto text-gray-300 dark:text-[#1a3884]/40 mb-3" />
                     <p className="text-sm font-medium text-gray-600 dark:text-gray-300">
                       {studentSearch ? "No students found matching your search." : "No students found for this college."}
                     </p>
                  </div>
                )}
              </div>
            </Card>
          </div>
        </div>
      )}

      {activeTab === 'reports' && (
        <div className="animate-fade-in">
          <Card title="Export College Summary">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
              <div className="p-6 border-2 border-dashed border-gray-200 dark:border-[#1a3884]/40 rounded-2xl hover:border-[#1a3884]/50 dark:hover:border-blue-500 bg-gray-50/50 dark:bg-[#00152E]/30 transition-colors flex flex-col items-center text-center">
                <FileText className="w-10 h-10 text-gray-400 dark:text-[#1a3884]/50 mb-3" />
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Export as PDF</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
                  Comprehensive PDF report with college information and users summary.
                </p>
                <button
                  type="button"
                  onClick={exportCollegeSummaryPDF}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#1a3884] text-white hover:bg-[#0f225c] transition-colors"
                >
                  <Download className="w-4 h-4" /> Download PDF
                </button>
              </div>

              <div className="p-6 border-2 border-dashed border-gray-200 dark:border-[#1a3884]/40 rounded-2xl hover:border-[#1a3884]/50 dark:hover:border-blue-500 bg-gray-50/50 dark:bg-[#00152E]/30 transition-colors flex flex-col items-center text-center">
                <FileText className="w-10 h-10 text-gray-400 dark:text-[#1a3884]/50 mb-3" />
                <h3 className="text-base font-bold text-gray-900 dark:text-white mb-1">Export as Excel</h3>
                <p className="text-xs text-gray-500 dark:text-gray-400 mb-4 max-w-sm">
                  Excel workbook with separate sheets for college info and users data.
                </p>
                <button
                  type="button"
                  onClick={exportCollegeSummaryExcel}
                  className="inline-flex items-center gap-1.5 px-4 py-2 rounded-lg text-sm font-semibold bg-[#1a3884] text-white hover:bg-[#0f225c] transition-colors"
                >
                  <Download className="w-4 h-4" /> Download Excel
                </button>
              </div>
            </div>
          </Card>
        </div>
      )}

      {/* Document Preview Modal */}
      <Modal
        isOpen={!!previewDoc}
        onClose={() => setPreviewDoc(null)}
        title={previewDoc?.title || 'Document Preview'}
        size="2xl"
      >
        {previewDoc && (
          <div className="flex flex-col items-center gap-4">
            {previewDoc.type === 'video' ? (
              <video src={previewDoc.url} controls className="w-full max-h-[70vh] rounded-xl bg-black" />
            ) : (
              <img src={previewDoc.url} alt={previewDoc.title} className="w-full max-h-[70vh] object-contain rounded-xl bg-gray-50 dark:bg-[#00152E]/40" />
            )}
            <a
              href={previewDoc.url}
              target="_blank"
              rel="noopener noreferrer"
              className="inline-flex items-center gap-2 text-sm font-semibold text-[#1a3884] dark:text-[#4f7dff] hover:underline"
            >
              <Download className="w-4 h-4" /> Open / Download original
            </a>
          </div>
        )}
      </Modal>
    </div>
  );
};

export default CollegeProfile;
